import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Building2, Upload, Trash2, Check, Image as ImageIcon, FileText, CreditCard, Wand2, BrainCircuit, Palette } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"
];

const PAYMENT_TERMS = [
  "Due on Receipt",
  "Net 15",
  "Net 30",
  "Net 45",
  "Net 60",
  "Net 90"
];

export default function SettingsPage({ companyId, company, refreshCompanies }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  
  // Company basic info
  const [name, setName] = useState("");
  const [ein, setEin] = useState("");
  const [businessType, setBusinessType] = useState("Sole Proprietorship");
  
  // Contact info
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  
  // Address
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  
  // Invoice settings
  const [defaultTaxRate, setDefaultTaxRate] = useState("0");
  const [defaultPaymentTerms, setDefaultPaymentTerms] = useState("Due on Receipt");
  const [paymentInstructions, setPaymentInstructions] = useState("");
  
  // AI Classification settings
  const [aiEnabled, setAiEnabled] = useState(true);
  const [confidenceThreshold, setConfidenceThreshold] = useState(70);
  const [autoClassifyOnSync, setAutoClassifyOnSync] = useState(true);
  const [requireReviewBelowThreshold, setRequireReviewBelowThreshold] = useState(true);
  const [aiSettingsLoading, setAiSettingsLoading] = useState(false);
  const [aiSettingsSaving, setAiSettingsSaving] = useState(false);
  
  // Brand colors
  const [brandPrimaryColor, setBrandPrimaryColor] = useState("#2563eb");
  const [brandSecondaryColor, setBrandSecondaryColor] = useState("#1e40af");
  const [brandAccentColor, setBrandAccentColor] = useState("#e85d24");

  useEffect(() => {
    if (company) {
      setName(company.name || "");
      setEin(company.ein || "");
      setBusinessType(company.business_type || "Sole Proprietorship");
      setPhone(company.phone || "");
      setEmail(company.email || "");
      setWebsite(company.website || "");
      setStreetAddress(company.street_address || "");
      setCity(company.city || "");
      setState(company.state || "");
      setZipCode(company.zip_code || "");
      setDefaultTaxRate(String(company.default_tax_rate || 0));
      setDefaultPaymentTerms(company.default_payment_terms || "Due on Receipt");
      setPaymentInstructions(company.payment_instructions || "");
      // Brand colors
      setBrandPrimaryColor(company.brand_primary_color || "#2563eb");
      setBrandSecondaryColor(company.brand_secondary_color || "#1e40af");
      setBrandAccentColor(company.brand_accent_color || "#e85d24");
    }
  }, [company]);

  // Load AI settings
  useEffect(() => {
    if (companyId) {
      loadAiSettings();
    }
  }, [companyId]);

  const loadAiSettings = async () => {
    setAiSettingsLoading(true);
    try {
      const res = await api.get(`/companies/${companyId}/ai-settings`);
      setAiEnabled(res.data.ai_classification_enabled);
      setConfidenceThreshold(Math.round(res.data.confidence_threshold * 100));
      setAutoClassifyOnSync(res.data.auto_classify_on_sync);
      setRequireReviewBelowThreshold(res.data.require_review_below_threshold);
    } catch (err) {
      // Settings might not exist yet, use defaults
    } finally {
      setAiSettingsLoading(false);
    }
  };

  const handleSaveAiSettings = async () => {
    setAiSettingsSaving(true);
    try {
      await api.put(`/companies/${companyId}/ai-settings`, null, {
        params: {
          ai_classification_enabled: aiEnabled,
          confidence_threshold: confidenceThreshold / 100,
          auto_classify_on_sync: autoClassifyOnSync,
          require_review_below_threshold: requireReviewBelowThreshold
        }
      });
      toast.success("AI settings saved");
    } catch (err) {
      toast.error("Failed to save AI settings");
    } finally {
      setAiSettingsSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload PNG, JPG, or SVG.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 5MB.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      await api.post(`/companies/${companyId}/logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Logo uploaded successfully");
      refreshCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to upload logo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteLogo = async () => {
    setDeleting(true);
    try {
      await api.delete(`/companies/${companyId}/logo`);
      toast.success("Logo deleted");
      refreshCompanies();
    } catch (err) {
      toast.error("Failed to delete logo");
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveCompany = async () => {
    if (!name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/companies/${companyId}`, {
        name: name.trim(),
        ein: ein.trim(),
        business_type: businessType,
        phone: phone.trim(),
        email: email.trim(),
        website: website.trim(),
        street_address: streetAddress.trim(),
        city: city.trim(),
        state: state,
        zip_code: zipCode.trim(),
        default_tax_rate: parseFloat(defaultTaxRate) || 0,
        default_payment_terms: defaultPaymentTerms,
        payment_instructions: paymentInstructions.trim(),
        brand_primary_color: brandPrimaryColor,
        brand_secondary_color: brandSecondaryColor,
        brand_accent_color: brandAccentColor,
      });
      toast.success("Company settings saved");
      refreshCompanies();
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const logoUrl = company?.logo_url ? `${BACKEND_URL}/api${company.logo_url}` : null;

  if (!companyId) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Select a company to view settings
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-[#d4c4b0]">Company Settings</h1>
        <p className="text-[#8b9299] text-sm mt-1">Manage your company profile, branding, and invoice settings</p>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="bg-secondary mb-6">
          <TabsTrigger value="profile" className="data-[state=active]:bg-[#e85d24]">
            <Building2 className="w-4 h-4 mr-2" /> Profile
          </TabsTrigger>
          <TabsTrigger value="branding" className="data-[state=active]:bg-[#e85d24]">
            <Palette className="w-4 h-4 mr-2" /> Branding
          </TabsTrigger>
          <TabsTrigger value="invoicing" className="data-[state=active]:bg-[#e85d24]">
            <FileText className="w-4 h-4 mr-2" /> Invoicing
          </TabsTrigger>
          <TabsTrigger value="ai" className="data-[state=active]:bg-[#e85d24]">
            <BrainCircuit className="w-4 h-4 mr-2" /> AI Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          {/* Logo Section */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base font-heading text-[#d4c4b0] flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-[#e85d24]" />
                Company Logo
              </CardTitle>
              <CardDescription className="text-[#8b9299]">
                Upload your logo for branded invoices and reports (PNG, JPG, SVG - max 5MB)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6">
                <div className="w-32 h-32 rounded-lg border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-secondary/50" data-testid="logo-preview">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Company logo" className="w-full h-full object-contain p-2" />
                  ) : (
                    <div className="text-center text-[#6b7280]">
                      <Building2 className="w-10 h-10 mx-auto mb-1 opacity-50" />
                      <span className="text-xs">No logo</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-3">
                  <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.svg" onChange={handleLogoUpload} className="hidden" id="logo-upload" data-testid="logo-file-input" />
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="border-border text-[#d4c4b0]" data-testid="upload-logo-btn">
                      {uploading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Uploading...</> : <><Upload className="w-4 h-4 mr-2" />{logoUrl ? "Change Logo" : "Upload Logo"}</>}
                    </Button>
                    {logoUrl && (
                      <Button variant="outline" onClick={handleDeleteLogo} disabled={deleting} className="border-destructive/50 text-destructive hover:bg-destructive/10" data-testid="delete-logo-btn">
                        {deleting ? <div className="w-4 h-4 border-2 border-destructive border-t-transparent rounded-full animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg border border-border/50">
                    <p className="text-xs text-[#8b9299]">
                      <span className="text-[#d4c4b0] font-medium">Recommended size:</span> 400×150 pixels (or similar 8:3 aspect ratio)
                    </p>
                    <p className="text-xs text-[#6b7280] mt-1">
                      For best results, use a transparent PNG with your logo horizontally oriented. Square logos work best at 200×200 pixels.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Details */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base font-heading text-[#d4c4b0] flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#e85d24]" />
                Business Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#8b9299] text-sm">Company Name *</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Corp" className="mt-1.5 bg-secondary border-border text-white" data-testid="company-name-input" />
                </div>
                <div>
                  <Label className="text-[#8b9299] text-sm">EIN</Label>
                  <Input value={ein} onChange={(e) => setEin(e.target.value)} placeholder="XX-XXXXXXX" className="mt-1.5 bg-secondary border-border text-white" data-testid="company-ein-input" />
                </div>
                <div>
                  <Label className="text-[#8b9299] text-sm">Business Type</Label>
                  <Select value={businessType} onValueChange={setBusinessType}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="business-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {["Sole Proprietorship", "LLC", "S-Corp", "C-Corp", "Partnership"].map(t => (
                        <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator className="bg-border/50 my-4" />
              <h4 className="text-sm font-medium text-[#d4c4b0]">Contact Information</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-[#8b9299] text-sm">Phone</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" className="mt-1.5 bg-secondary border-border text-white" data-testid="company-phone-input" />
                </div>
                <div>
                  <Label className="text-[#8b9299] text-sm">Email</Label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contact@company.com" className="mt-1.5 bg-secondary border-border text-white" data-testid="company-email-input" />
                </div>
                <div>
                  <Label className="text-[#8b9299] text-sm">Website</Label>
                  <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="www.company.com" className="mt-1.5 bg-secondary border-border text-white" data-testid="company-website-input" />
                </div>
              </div>

              <Separator className="bg-border/50 my-4" />
              <h4 className="text-sm font-medium text-[#d4c4b0]">Business Address</h4>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-[#8b9299] text-sm">Street Address</Label>
                  <Input value={streetAddress} onChange={(e) => setStreetAddress(e.target.value)} placeholder="123 Main Street, Suite 100" className="mt-1.5 bg-secondary border-border text-white" data-testid="company-street-input" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2 md:col-span-2">
                    <Label className="text-[#8b9299] text-sm">City</Label>
                    <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="New York" className="mt-1.5 bg-secondary border-border text-white" data-testid="company-city-input" />
                  </div>
                  <div>
                    <Label className="text-[#8b9299] text-sm">State</Label>
                    <Select value={state} onValueChange={setState}>
                      <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="company-state-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border max-h-60">
                        {US_STATES.map(s => (
                          <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#8b9299] text-sm">ZIP Code</Label>
                    <Input value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="10001" className="mt-1.5 bg-secondary border-border text-white" data-testid="company-zip-input" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base font-heading text-[#d4c4b0] flex items-center gap-2">
                <Palette className="w-5 h-5 text-[#e85d24]" />
                Brand Colors
              </CardTitle>
              <CardDescription className="text-[#8b9299]">
                Customize your company's color palette for invoices, quotes, and reports
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Primary Color */}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label className="text-[#d4c4b0]">Primary Color</Label>
                  <p className="text-xs text-[#6b7280] mt-0.5">Used for headers, titles, and main accents in documents</p>
                </div>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg border-2 border-border cursor-pointer shadow-inner"
                    style={{ backgroundColor: brandPrimaryColor }}
                    onClick={() => document.getElementById('primary-color-input').click()}
                  />
                  <input 
                    type="color" 
                    id="primary-color-input"
                    value={brandPrimaryColor} 
                    onChange={(e) => setBrandPrimaryColor(e.target.value)}
                    className="sr-only"
                  />
                  <Input 
                    value={brandPrimaryColor} 
                    onChange={(e) => setBrandPrimaryColor(e.target.value)}
                    className="w-28 bg-secondary border-border text-white font-mono text-sm"
                    placeholder="#2563eb"
                    data-testid="brand-primary-color"
                  />
                </div>
              </div>

              <Separator className="bg-border/50" />

              {/* Secondary Color */}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label className="text-[#d4c4b0]">Secondary Color</Label>
                  <p className="text-xs text-[#6b7280] mt-0.5">Used for table headers and secondary elements</p>
                </div>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg border-2 border-border cursor-pointer shadow-inner"
                    style={{ backgroundColor: brandSecondaryColor }}
                    onClick={() => document.getElementById('secondary-color-input').click()}
                  />
                  <input 
                    type="color" 
                    id="secondary-color-input"
                    value={brandSecondaryColor} 
                    onChange={(e) => setBrandSecondaryColor(e.target.value)}
                    className="sr-only"
                  />
                  <Input 
                    value={brandSecondaryColor} 
                    onChange={(e) => setBrandSecondaryColor(e.target.value)}
                    className="w-28 bg-secondary border-border text-white font-mono text-sm"
                    placeholder="#1e40af"
                    data-testid="brand-secondary-color"
                  />
                </div>
              </div>

              <Separator className="bg-border/50" />

              {/* Accent Color */}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label className="text-[#d4c4b0]">Accent Color</Label>
                  <p className="text-xs text-[#6b7280] mt-0.5">Used for highlights, totals, and call-to-action elements</p>
                </div>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg border-2 border-border cursor-pointer shadow-inner"
                    style={{ backgroundColor: brandAccentColor }}
                    onClick={() => document.getElementById('accent-color-input').click()}
                  />
                  <input 
                    type="color" 
                    id="accent-color-input"
                    value={brandAccentColor} 
                    onChange={(e) => setBrandAccentColor(e.target.value)}
                    className="sr-only"
                  />
                  <Input 
                    value={brandAccentColor} 
                    onChange={(e) => setBrandAccentColor(e.target.value)}
                    className="w-28 bg-secondary border-border text-white font-mono text-sm"
                    placeholder="#e85d24"
                    data-testid="brand-accent-color"
                  />
                </div>
              </div>

              <Separator className="bg-border/50" />

              {/* Preview Section */}
              <div>
                <Label className="text-[#d4c4b0] mb-3 block">Preview</Label>
                <div className="p-4 bg-white rounded-lg border border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded" style={{ backgroundColor: brandPrimaryColor }} />
                    <span className="font-semibold" style={{ color: brandPrimaryColor }}>Invoice #INV-001</span>
                  </div>
                  <div className="rounded overflow-hidden mb-3">
                    <div className="p-2 text-white text-sm font-medium" style={{ backgroundColor: brandSecondaryColor }}>
                      Line Items
                    </div>
                    <div className="bg-gray-50 p-2 text-sm text-gray-600">
                      Consulting Services - $1,500.00
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <span className="font-bold" style={{ color: brandAccentColor }}>Total: $1,500.00</span>
                  </div>
                </div>
                <p className="text-xs text-[#6b7280] mt-2">
                  This is a simplified preview. Your actual documents will include your logo and full formatting.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Color Presets */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base font-heading text-[#d4c4b0]">Quick Presets</CardTitle>
              <CardDescription className="text-[#8b9299]">
                Select a pre-designed color scheme
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { name: "Professional Blue", primary: "#2563eb", secondary: "#1e40af", accent: "#e85d24" },
                  { name: "Corporate Green", primary: "#059669", secondary: "#047857", accent: "#f59e0b" },
                  { name: "Modern Purple", primary: "#7c3aed", secondary: "#5b21b6", accent: "#ec4899" },
                  { name: "Classic Black", primary: "#1f2937", secondary: "#374151", accent: "#dc2626" },
                ].map((preset, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setBrandPrimaryColor(preset.primary);
                      setBrandSecondaryColor(preset.secondary);
                      setBrandAccentColor(preset.accent);
                    }}
                    className="p-3 rounded-lg border border-border bg-secondary/30 hover:bg-secondary/50 transition-colors text-left"
                    data-testid={`preset-${idx}`}
                  >
                    <div className="flex gap-1 mb-2">
                      <div className="w-5 h-5 rounded" style={{ backgroundColor: preset.primary }} />
                      <div className="w-5 h-5 rounded" style={{ backgroundColor: preset.secondary }} />
                      <div className="w-5 h-5 rounded" style={{ backgroundColor: preset.accent }} />
                    </div>
                    <span className="text-xs text-[#8b9299]">{preset.name}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoicing" className="space-y-6">
          {/* Invoice Settings */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base font-heading text-[#d4c4b0] flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#e85d24]" />
                Invoice & Quote Settings
              </CardTitle>
              <CardDescription className="text-[#8b9299]">
                Configure default settings for invoices and quotes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#8b9299] text-sm">Default Tax Rate (%)</Label>
                  <Input type="number" step="0.01" min="0" max="100" value={defaultTaxRate} onChange={(e) => setDefaultTaxRate(e.target.value)} placeholder="0.00" className="mt-1.5 bg-secondary border-border text-white" data-testid="tax-rate-input" />
                  <p className="text-xs text-[#6b7280] mt-1">Can be overridden per invoice</p>
                </div>
                <div>
                  <Label className="text-[#8b9299] text-sm">Default Payment Terms</Label>
                  <Select value={defaultPaymentTerms} onValueChange={setDefaultPaymentTerms}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="payment-terms-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {PAYMENT_TERMS.map(t => (
                        <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="p-3 bg-secondary/50 rounded-lg border border-border">
                <p className="text-sm text-[#8b9299]">
                  <span className="text-[#d4c4b0] font-medium">Document Numbering:</span> Quotes use format <code className="text-[#e85d24]">QT1234567</code> and invoices use <code className="text-[#e85d24]">INV1234567</code>. When a quote is converted to an invoice, the number is preserved (e.g., QT1234567 → INV1234567).
                </p>
              </div>
              
              <div>
                <Label className="text-[#8b9299] text-sm">Payment Instructions</Label>
                <Textarea value={paymentInstructions} onChange={(e) => setPaymentInstructions(e.target.value)} placeholder="Bank: ABC Bank&#10;Account: 1234567890&#10;Routing: 987654321&#10;Or mail check to: 123 Main St..." className="mt-1.5 bg-secondary border-border text-white min-h-[100px]" data-testid="payment-instructions-textarea" />
                <p className="text-xs text-[#6b7280] mt-1">This will appear on your invoices</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Settings Tab */}
        <TabsContent value="ai" className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base font-heading text-[#d4c4b0] flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-[#e85d24]" />
                AI Transaction Classification
              </CardTitle>
              <CardDescription className="text-[#8b9299]">
                Configure how AI automatically categorizes your transactions using IRS Schedule C categories
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable AI Classification */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-[#d4c4b0]">AI Classification</Label>
                  <p className="text-xs text-[#6b7280]">Enable AI-powered transaction categorization</p>
                </div>
                <Switch
                  checked={aiEnabled}
                  onCheckedChange={setAiEnabled}
                  disabled={aiSettingsLoading}
                  data-testid="ai-enabled-switch"
                />
              </div>
              
              <Separator className="bg-border/50" />
              
              {/* Auto-classify on Sync */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-[#d4c4b0]">Auto-classify on Sync</Label>
                  <p className="text-xs text-[#6b7280]">Automatically categorize transactions when syncing from bank</p>
                </div>
                <Switch
                  checked={autoClassifyOnSync}
                  onCheckedChange={setAutoClassifyOnSync}
                  disabled={aiSettingsLoading || !aiEnabled}
                  data-testid="auto-classify-switch"
                />
              </div>
              
              <Separator className="bg-border/50" />
              
              {/* Confidence Threshold */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-[#d4c4b0]">Confidence Threshold</Label>
                    <p className="text-xs text-[#6b7280]">
                      Minimum confidence required to accept AI classification
                    </p>
                  </div>
                  <span className="text-lg font-semibold text-[#e85d24]">{confidenceThreshold}%</span>
                </div>
                <Slider
                  value={[confidenceThreshold]}
                  onValueChange={([value]) => setConfidenceThreshold(value)}
                  min={50}
                  max={95}
                  step={5}
                  disabled={aiSettingsLoading || !aiEnabled}
                  className="w-full"
                  data-testid="confidence-slider"
                />
                <div className="flex justify-between text-xs text-[#6b7280]">
                  <span>50% (Accept more)</span>
                  <span>95% (Strict)</span>
                </div>
              </div>
              
              <Separator className="bg-border/50" />
              
              {/* Require Review */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-[#d4c4b0]">Review Low-Confidence</Label>
                  <p className="text-xs text-[#6b7280]">
                    Flag transactions below threshold for manual review
                  </p>
                </div>
                <Switch
                  checked={requireReviewBelowThreshold}
                  onCheckedChange={setRequireReviewBelowThreshold}
                  disabled={aiSettingsLoading || !aiEnabled}
                  data-testid="require-review-switch"
                />
              </div>
              
              {/* Save AI Settings */}
              <div className="pt-4">
                <Button 
                  onClick={handleSaveAiSettings} 
                  disabled={aiSettingsSaving || aiSettingsLoading}
                  className="btn-primary-gradient w-full"
                  data-testid="save-ai-settings-btn"
                >
                  {aiSettingsSaving ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Saving...</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" />Save AI Settings</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Info Card */}
          <Card className="bg-[#141b2d] border-border/50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <BrainCircuit className="w-5 h-5 text-[#e85d24] mt-0.5" />
                <div>
                  <p className="text-sm text-[#d4c4b0] font-medium">How AI Classification Works</p>
                  <p className="text-xs text-[#6b7280] mt-1">
                    Our AI analyzes transaction descriptions and amounts to automatically assign 
                    IRS Schedule C categories. Transactions are classified when synced from your 
                    bank or when you click "AI Classify" on the Transactions page. 
                    Categories below your confidence threshold will be flagged for your review.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSaveCompany} disabled={saving || !name.trim()} className="btn-primary-gradient" data-testid="save-settings-btn">
          {saving ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Saving...</> : <><Check className="w-4 h-4 mr-2" />Save All Settings</>}
        </Button>
      </div>
    </div>
  );
}
